import React, { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { moduleText as ui } from "../i18n/moduleText";
import { EMAIL_PROVIDERS, buildEmailComposeUrl, emailProviderStorageKey, gmailAccountStorageKey, isEmailProvider, isGmailAccountChoice, readEmailProvider, readGmailAccount, writeEmailProvider, writeGmailAccount } from "../utils/emailComposer";
import "../components/EmailComposer.css";

const EmailComposerContext = createContext(null);

export function useEmailComposer() {
  const context = useContext(EmailComposerContext);
  if (!context) throw new Error("useEmailComposer must be used within EmailComposerProvider.");
  return context;
}

export function EmailComposerProvider({ children }) {
  const { currentUser } = useAuth() || {};
  const accountId = currentUser?.uid || "guest";
  return <EmailComposerSession key={accountId} accountId={accountId}>{children}</EmailComposerSession>;
}

function EmailComposerSession({ accountId, children }) {
  useTranslation();
  const [provider, setProvider] = useState(() => readEmailProvider(accountId));
  const [gmailAccount, setGmailAccount] = useState(() => readGmailAccount(accountId));
  const [dialog, setDialog] = useState(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const sync = (event) => {
      if (event.key === null || event.key === emailProviderStorageKey(accountId)) {
        setProvider(readEmailProvider(accountId));
        setNotice("");
      }
      if (event.key === null || event.key === gmailAccountStorageKey(accountId)) {
        setGmailAccount(readGmailAccount(accountId));
        setNotice("");
      }
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [accountId]);

  function saveProvider(next) {
    if (next && !isEmailProvider(next)) return;
    const saved = writeEmailProvider(accountId, next);
    setProvider(next);
    setNotice(saved ? ui("Email preference saved.") : ui("Your browser could not save this choice. It will work for this session."));
    return saved;
  }

  function saveGmailAccount(next) {
    if (!isGmailAccountChoice(next)) return;
    const saved = writeGmailAccount(accountId, next);
    setGmailAccount(next);
    setNotice(saved ? ui("Email preference saved.") : ui("Your browser could not save this choice. It will work for this session."));
    return saved;
  }

  function launch(selected, draft, beforeOpen, saved = true, account = gmailAccount) {
    const url = buildEmailComposeUrl(selected, draft, account);
    if (beforeOpen?.({ ...draft, mailtoUrl: buildEmailComposeUrl("system", draft), composeUrl: url, provider: selected, gmailAccount: selected === "gmail" ? account : null }) === false) return;
    if (selected === "system") window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
    setNotice((saved ? "" : `${ui("Your browser could not save this choice. It will work for this session.")} `) + ui("Email draft requested. Review and send it in your email provider."));
  }

  function composeEmail(draft = {}, beforeOpen) {
    if (provider && (provider !== "gmail" || gmailAccount)) launch(provider, draft, beforeOpen);
    else setDialog({ draft, beforeOpen });
  }

  const value = { provider, gmailAccount, notice, composeEmail, saveProvider, saveGmailAccount, changeProvider: () => setDialog({}) };
  return <EmailComposerContext.Provider value={value}>
    {children}
    {dialog && <EmailProviderDialog provider={provider} gmailAccount={gmailAccount} composing={Boolean(dialog.draft)} onCancel={() => setDialog(null)} onConfirm={(selected, remember, account) => {
      let saved = true;
      if (remember || !dialog.draft) {
        saved = saveProvider(selected);
        if (selected === "gmail") saved = saveGmailAccount(account) && saved;
      }
      if (dialog.draft) launch(selected, dialog.draft, dialog.beforeOpen, saved, account);
      setDialog(null);
    }} />}
  </EmailComposerContext.Provider>;
}

function GmailAccountFields({ choice, onChange, id }) {
  const mode = choice.mode;
  return <div className="sbs-email-account-fields">
    <label className="sbs-email-field" htmlFor={`${id}-account-mode`}>
      <span>{ui("Sending Google account")}</span>
      <select id={`${id}-account-mode`} value={mode} onChange={(event) => onChange({ ...choice, mode: event.target.value })}>
        <option value="choose">{ui("Choose a Google account each time")}</option>
        <option value="address">{ui("Use a specific Google account")}</option>
      </select>
    </label>
    {mode === "address" && <label className="sbs-email-field" htmlFor={`${id}-account-email`}>
      <span>{ui("Google account email")}</span>
      <input id={`${id}-account-email`} type="email" autoComplete="email" maxLength={254} required value={choice.email} placeholder="you@yourbusiness.com" onChange={(event) => onChange({ ...choice, email: event.target.value })} />
    </label>}
    <p className="sbs-email-help">{ui(mode === "address" ? "Use the address you sign in to Google with. Sign in to that account in this browser before opening a draft." : "Google will show its account chooser before opening your draft. You can select or add your business account there.")}</p>
    <p className="sbs-email-help">{ui("Check the sending address in Gmail before sending. Gmail aliases are selected inside Gmail.")}</p>
  </div>;
}

function EmailProviderDialog({ provider, gmailAccount, composing, onCancel, onConfirm }) {
  const [selected, setSelected] = useState(provider || "gmail");
  const [account, setAccount] = useState(() => gmailAccount || { mode: "choose", email: "" });
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(true);
  const ref = useRef(null);
  const id = useId();
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current.querySelector("select").focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  function handleKey(event) {
    if (event.key === "Escape") { event.preventDefault(); onCancel(); }
    if (event.key !== "Tab") return;
    const nodes = [...ref.current.querySelectorAll("select, input, button")];
    const first = nodes[0], last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
  function confirm(event) {
    event.preventDefault();
    const cleanAccount = { mode: account.mode, email: account.email.trim() };
    if (selected === "gmail" && !isGmailAccountChoice(cleanAccount)) {
      setError(ui("Enter a valid Google account email address."));
      return;
    }
    onConfirm(selected, remember, cleanAccount);
  }
  return createPortal(<div className="sbs-email-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
    <section ref={ref} className="sbs-email-dialog" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} onKeyDown={handleKey}>
      <h2 id={`${id}-title`}>{ui(composing ? "Choose your email provider" : "Change email provider")}</h2>
      <p id={`${id}-description`}>{ui("SBS opens a draft. You review and send it in your email provider.")}</p>
      <form onSubmit={confirm}>
      <label className="sbs-email-field" htmlFor={`${id}-provider`}>
        <span>{ui("Email provider")}</span>
        <select id={`${id}-provider`} value={selected} onChange={(event) => setSelected(event.target.value)}>
          {EMAIL_PROVIDERS.map((item) => <option key={item.id} value={item.id}>{ui(item.label)}</option>)}
        </select>
      </label>
      {selected === "gmail" && <GmailAccountFields choice={account} onChange={(next) => { setAccount(next); setError(""); }} id={id} />}
      {selected === "system" && <p className="sbs-email-help">{ui("Uses the email app selected in your browser or device settings.")}</p>}
      {composing && <label className="sbs-email-remember"><input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />{ui("Remember my choice")}</label>}
      <p className="sbs-email-help">{ui("Saved for this account on this browser. Change it here or in Account Settings at any time.")}</p>
      {error && <p className="sbs-email-error" role="alert">{error}</p>}
      <div className="sbs-email-dialog-actions">
        <button type="button" onClick={onCancel}>{ui("Cancel")}</button>
        <button type="submit" className="sbs-email-primary">{ui(composing ? "Open email draft" : "Save preference")}</button>
      </div>
      </form>
    </section>
  </div>, document.body);
}

export function EmailProviderControl({ disabled = false }) {
  useTranslation();
  const { provider, gmailAccount, changeProvider } = useEmailComposer();
  return <span className="sbs-email-provider-control">
    {provider && <span>{ui(EMAIL_PROVIDERS.find((item) => item.id === provider).label)}</span>}
    {provider === "gmail" && <span>{gmailAccount?.mode === "address" ? gmailAccount.email : ui("Google account chooser")}</span>}
    <button type="button" disabled={disabled} onClick={changeProvider}>{ui("Change email provider or account")}</button>
  </span>;
}

function GmailAccountSettings() {
  const { gmailAccount, saveGmailAccount } = useEmailComposer();
  const [choice, setChoice] = useState(() => gmailAccount || { mode: "choose", email: "" });
  const [error, setError] = useState("");
  const id = useId();
  useEffect(() => {
    setChoice(gmailAccount || { mode: "choose", email: "" });
    setError("");
  }, [gmailAccount]);
  function save(event) {
    event.preventDefault();
    const next = { mode: choice.mode, email: choice.email.trim() };
    if (!isGmailAccountChoice(next)) { setError(ui("Enter a valid Google account email address.")); return; }
    saveGmailAccount(next);
  }
  return <form className="sbs-email-account-settings" onSubmit={save}>
    <GmailAccountFields choice={choice} onChange={(next) => { setChoice(next); setError(""); }} id={id} />
    {error && <p className="sbs-email-error" role="alert">{error}</p>}
    <button type="submit" className="sbs-email-primary">{ui("Save Gmail account")}</button>
  </form>;
}

export function EmailProviderSettings() {
  useTranslation();
  const { provider, saveProvider, notice } = useEmailComposer();
  const id = useId();
  return <section className="settings-card" id="email-provider">
    <div className="settings-card-header"><h2>{ui("Email preferences")}</h2><p>{ui("Choose where SBS opens your customer emails and document drafts.")}</p></div>
    <label className="sbs-email-field" htmlFor={id}><span>{ui("Email provider")}</span>
      <select id={id} value={provider} onChange={(event) => saveProvider(event.target.value)}>
        <option value="">{ui("Ask every time")}</option>
        {EMAIL_PROVIDERS.map((item) => <option key={item.id} value={item.id}>{ui(item.label)}</option>)}
      </select>
    </label>
    {provider === "gmail" && <GmailAccountSettings />}
    <p className="sbs-email-help">{ui("Saved for this account on this browser. Change it here or in Account Settings at any time.")}</p>
    <p className="sbs-email-help">{ui("SBS opens a draft. You review and send it in your email provider.")}</p>
    {notice && <p className="sbs-email-status" role="status">{notice}</p>}
  </section>;
}
